import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";

import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SearchOptsGroup from "@plannel/pages/factory-plan/common/component/SearchOptsGroup";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";

import ItemJobChangeTab from "@plannel/pages/factory-plan/master/jobchangetime/ItemJobChangeTab";
import ItemGroupJobChangeTab from "@plannel/pages/factory-plan/master/jobchangetime/ItemGroupJobChangeTab";
import RouteJobChangeTab from "@plannel/pages/factory-plan/master/jobchangetime/RouteJobChangeTab";
import RouteGroupJobChangeTab from "@plannel/pages/factory-plan/master/jobchangetime/RouteGroupJobChangeTab";
import ResourceJobChangeTab from "@plannel/pages/factory-plan/master/jobchangetime/ResourceJobChangeTab";

// 이 화면의 고정 조회조건 라벨 키(§10 — DB 동적 표시 제어 제거).
const SEARCH_FIELD_LABELS = {
  resourceCdNm: "FP_RESOURCE_CD",
  prevItemCd: "FP_PREV_ITEM_CD",
  nextItemCd: "FP_NEXT_ITEM_CD",
  prevItemGrpCd: "FP_PREV_ITEM_GRP_CD",
  nextItemGrpCd: "FP_NEXT_ITEM_GRP_CD",
  prevRouteCd: "FP_PREV_ROUTE_CD",
  nextRouteCd: "FP_NEXT_ROUTE_CD",
  prevRouteGrpCd: "FP_PREV_ROUTE_GRP_CD",
  nextRouteGrpCd: "FP_NEXT_ROUTE_GRP_CD",
};

// old tabSrhOptsInfo 대응 — 탭별로 노출할 조회조건 필드 목록만 다르게(§11-2).
const TAB_SEARCH_FIELDS = [
  ["resourceCdNm", "prevItemCd", "nextItemCd"],           // 0: 품목(Item) 탭
  ["resourceCdNm", "prevItemGrpCd", "nextItemGrpCd"],     // 1: 품목 그룹(ItemGroup) 탭
  ["resourceCdNm", "prevRouteCd", "nextRouteCd"],         // 2: 공정(Route) 탭
  ["resourceCdNm", "prevRouteGrpCd", "nextRouteGrpCd"],   // 3: 공정 그룹(RouteGroup) 탭
  ["resourceCdNm"],                                       // 4: 자원(Resource) 탭
];

const JobChangeTime = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [tabIndex, setTabIndex] = useState(0);
  const [searchFields, setSearchFields] = useState({
    resourceCdNm: reduxViewState?.resourceCdNm ?? "",
    prevItemCd: reduxViewState?.prevItemCd ?? "",
    nextItemCd: reduxViewState?.nextItemCd ?? "",
    prevItemGrpCd: reduxViewState?.prevItemGrpCd ?? "",
    nextItemGrpCd: reduxViewState?.nextItemGrpCd ?? "",
    prevRouteCd: reduxViewState?.prevRouteCd ?? "",
    nextRouteCd: reduxViewState?.nextRouteCd ?? "",
    prevRouteGrpCd: reduxViewState?.prevRouteGrpCd ?? "",
    nextRouteGrpCd: reduxViewState?.nextRouteGrpCd ?? "",
  });

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { planScope, ...searchFields });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, searchFields]);

  const onSearchFieldChange = (tpCd, value) => setSearchFields(prev => ({ ...prev, [tpCd]: value }));

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <SearchOptsGroup
          fields={searchFields}
          fieldKeys={TAB_SEARCH_FIELDS[tabIndex]}
          onChange={onSearchFieldChange}
          labels={SEARCH_FIELD_LABELS}
        />
        {/* FilterContainer는 마지막 자식을 float:right(+필터 토글 아이콘)로 띄운다 — SearchOptsGroup이
            마지막이면 PlanScope와 큰 간격이 생기므로, 빈 Box를 마지막에 둬 그 역할을 넘긴다. */}
        <Box />
      </FilterContainer>
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 38 }}
      >
        <Tab label={t("FP_ITEM_JOB_CHANGE")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
        <Tab label={t("FP_ITEM_GROUP_JOB_CHANGE")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
        <Tab label={t("FP_ROUTE_JOB_CHANGE")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
        <Tab label={t("FP_ROUTE_GROUP_JOB_CHANGE")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
        <Tab label={t("FP_RESOURCE_JOB_CHANGE")} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />
      </Tabs>
      {/* §11-1: 탭은 항상 마운트하고 display로만 숨긴다 — 그리드 dirty-row/스크롤/선택 상태 보존. */}
      <Box sx={{ display: tabIndex === 0 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <ItemJobChangeTab viewName={`${viewName}_ITEM`} planScope={planScope} searchFields={searchFields} />
      </Box>
      <Box sx={{ display: tabIndex === 1 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <ItemGroupJobChangeTab viewName={`${viewName}_ITEM_GROUP`} planScope={planScope} searchFields={searchFields} />
      </Box>
      <Box sx={{ display: tabIndex === 2 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <RouteJobChangeTab viewName={`${viewName}_ROUTE`} planScope={planScope} searchFields={searchFields} />
      </Box>
      <Box sx={{ display: tabIndex === 3 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <RouteGroupJobChangeTab viewName={`${viewName}_ROUTE_GROUP`} planScope={planScope} searchFields={searchFields} />
      </Box>
      <Box sx={{ display: tabIndex === 4 ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <ResourceJobChangeTab viewName={`${viewName}_RESOURCE`} planScope={planScope} searchFields={searchFields} />
      </Box>
    </>
  );
};

export default withTranslation()(JobChangeTime);
